import React, { createContext, useContext, ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useUserInfo } from "../hooks/useUserInfo";
import type { UserContext } from "../hooks/useUserInfo";
import { setUserContext, setHotelTags, clearUser } from "../utils/sentry";
import { identifyUser, resetAnalytics, setAnalyticsContext } from "../utils/analytics";

interface UserInfoContextType {
  userInfo: UserContext | null;
  hotelId: string | undefined;
  employeeId: string | undefined;
  platformUserId: string | undefined;
}

const UserInfoContext = createContext<UserInfoContextType | undefined>(
  undefined
);

export const UserInfoProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { userInfo, isLoading, error } = useUserInfo();
  const location = useLocation();

  // Check if we're on an error page - this must be checked first
  const isErrorPage =
    location.pathname === "/access-denied" ||
    location.pathname === "/employee-access-revoked";

  // Update Sentry and Analytics context when user info changes
  useEffect(() => {
    const updateContexts = async () => {
      if (userInfo) {
        // Set user context for Sentry (async due to email hashing)
        await setUserContext({
          id: userInfo.platformUserId,
          email: userInfo.primaryEmail,
        });

        // Set hotel and employee context for Sentry
        setHotelTags({
          hotelId: userInfo.hotelId,
          employeeId: userInfo.employeeId,
        });

        // Set user context for Analytics (privacy-safe)
        identifyUser({
          id: userInfo.platformUserId,
          role: userInfo.roleId,
          hotelId: userInfo.hotelId,
          employeeId: userInfo.employeeId,
        });

        // Set additional analytics context
        setAnalyticsContext({
          environment: import.meta.env.MODE,
          appVersion: import.meta.env.PACKAGE_VERSION || 'unknown',
        });
      } else {
        // Clear user context when no user info (e.g., logout)
        clearUser();
        resetAnalytics();
      }
    };

    updateContexts().catch(console.error);

    // Cleanup function to clear user context on unmount
    return () => {
      clearUser();
      resetAnalytics();
    };
  }, [userInfo]);

  // Allow rendering on error pages even without userInfo - this check must come FIRST
  // and take precedence over all other states (loading, error, etc.)
  // This early return prevents any error screens from showing on these routes
  if (isErrorPage) {
    return (
      <UserInfoContext.Provider
        value={{
          userInfo: null,
          hotelId: undefined,
          employeeId: undefined,
          platformUserId: undefined,
        }}
      >
        {children}
      </UserInfoContext.Provider>
    );
  }

  if (isLoading && !userInfo) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <p className="text-lg text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  // If an error occurred, show a generic error screen to avoid blank page.
  // Note: This won't trigger if we're on an error page (checked above)
  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', textAlign: 'center' }}>
        <h1 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>Something went wrong</h1>
        <p style={{ color: '#6b7280', maxWidth: '420px' }}>
          We couldn't load your user profile. Please refresh the page or try again later.
        </p>
        <button
          style={{ marginTop: '16px', background: '#3b82f6', color: 'white', padding: '8px 16px', borderRadius: '4px' }}
          onClick={() => window.location.reload()}
        >
          Refresh
        </button>
      </div>
    );
  }

  // Render the app with cached data, showing minimal loader if still fetching
  return (
    <UserInfoContext.Provider
      value={{
        userInfo,
        hotelId: userInfo?.hotelId,
        employeeId: userInfo?.employeeId,
        platformUserId: userInfo?.platformUserId,
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserInfoContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserInfoProvider");
  }
  return context;
};
